import { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence, useMotionValue, useSpring, Variants } from 'framer-motion';
import { ChevronDown, EyeOff, Trash2, CloudOff, ArrowRight } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

// ═══════════════════════════════════════════════
// ألوان مستخرجة من اللوجو
// ═══════════════════════════════════════════════
const COLORS = {
  navy: '#1e3a5f',        // كحلي حرف P
  blue: '#2d6cdf',        // أزرق فاتح
  skyBlue: '#5b9bd5',     // أزرق سماوي
  green: '#4AB478',       // أخضر أساسي
  lime: '#8CC63F',        // أخضر ليموني (إشارة الواي فاي)
  brightLime: '#A3E635',  // أخضر ساطع
};

const TRUST_BADGES = [
  { icon: EyeOff, label: "Zero Background Tracking", detail: "Privacy by design" },
  { icon: Trash2, label: "Auto-Delete on Check-in", detail: "Data wiped instantly" },
  { icon: CloudOff, label: "Cloud-Independent Alerts", detail: "Alerts work offline" },
];

// الـ Hook مقسوم بذكاء: سطرين + كلمات مميزة ملونة
const HOOK_LINES = [
  [
    { text: "Safety", highlight: 'blue' },
    { text: "should", highlight: null },
    { text: "not", highlight: null },
    { text: "require", highlight: null },
  ],
  [
    { text: "sacrificing", highlight: null },
    { text: "privacy.", highlight: 'green' },
  ],
];

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] },
  },
};

// ═══════════════════════════════════════════════
// أنيميشن حرف-بحرف للعنوان
// ═══════════════════════════════════════════════
const letterContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.5 } },
};

const letterVariant: Variants = {
  hidden: { opacity: 0, y: 60, rotateX: -90, filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

// أنيميشن كلمة-كلمة للـ Hook
const wordContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 1.3 } },
};

const wordVariant: Variants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  const [hasScrolledPast, setHasScrolledPast] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  // ── Parallax بالماوس ──
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20 });
  const bgX = useTransform(smoothX, [-0.5, 0.5], [15, -15]);
  const bgY = useTransform(smoothY, [-0.5, 0.5], [15, -15]);
  const logoX = useTransform(smoothX, [-0.5, 0.5], [-10, 10]);
  const logoY = useTransform(smoothY, [-0.5, 0.5], [-10, 10]);

  const { scrollYProgress, scrollY } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  useEffect(() => {
    return scrollY.onChange((latest) => {
      if (latest > 50 && !hasScrolledPast) setHasScrolledPast(true);
    });
  }, [scrollY, hasScrolledPast]);

  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.8], [1, 0.95]);
  const y = useTransform(scrollYProgress, [0, 0.8], [0, 100]);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen w-full overflow-hidden bg-[#0a1628] pb-24"
    >
      {/* ═══════════ الخلفية + الجزيئات ═══════════ */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ opacity }}>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#0f2440] to-[#0a1628]" />

        {/* هالة خضراء متحركة مع الماوس */}
        <motion.div
          className="absolute inset-0"
          style={{
            x: bgX,
            y: bgY,
            background: `radial-gradient(ellipse 80% 50% at 50% 30%, rgba(74, 180, 120, 0.15) 0%, transparent 70%)`,
          }}
        />

        {/* هالة زرقاء ثانية — لربط اللون الأزرق من اللوجو بالخلفية */}
        <motion.div
          className="absolute inset-0"
          style={{
            x: useTransform(smoothX, [-0.5, 0.5], [-20, 20]),
            background: `radial-gradient(ellipse 60% 40% at 30% 60%, rgba(45, 108, 223, 0.08) 0%, transparent 70%)`,
          }}
        />

        {/* الجزيئات — بألوان اللوجو الاتنين */}
        {Array.from({ length: 35 }).map((_, i) => {
          const isGreen = i % 3 !== 0;
          return (
            <motion.div
              key={i}
              className="absolute rounded-full"
              initial={{ opacity: 0 }}
              animate={
                isMobile
                  ? { opacity: 0 } // Hide completely or don't animate on mobile
                  : {
                      y: [0, -30, 20, 0],
                      opacity: [0, 0.7, 0.3, 0],
                      x: [0, Math.random() * 30 - 15, Math.random() * 30 - 15, 0],
                      scale: [1, 1.5, 1, 1],
                    }
              }
              transition={
                isMobile
                  ? { duration: 0 }
                  : {
                      duration: 5 + Math.random() * 5,
                      repeat: Infinity,
                      delay: Math.random() * 4,
                      ease: "easeInOut",
                    }
              }
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: i % 5 === 0 ? '3px' : '2px',
                height: i % 5 === 0 ? '3px' : '2px',
                backgroundColor: isGreen ? 'rgba(140, 198, 63, 0.4)' : 'rgba(91, 155, 213, 0.4)',
                boxShadow: isGreen
                  ? '0 0 6px rgba(140, 198, 63, 0.6)'
                  : '0 0 6px rgba(91, 155, 213, 0.6)',
              }}
            />
          );
        })}
      </motion.div>

      {/* ═══════════ محتوى الـ Hero ═══════════ */}
      <motion.div
        className="relative z-10 text-center px-6 min-h-screen flex flex-col justify-center items-center"
        style={{ opacity, scale, y }}
      >
        {/* ── اللوجو ── */}
        <motion.div
          initial={{ scale: 0.6, opacity: 0, y: 30 }}
          animate={loaded ? { scale: 1, opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6"
        >
          <div className="relative inline-block mb-4">
            {/* هالة نابضة بلونين */}
            <div className="absolute inset-0 blur-3xl bg-[#4AB478]/25 rounded-full scale-150 animate-pulse-slow pointer-events-none" />
            <div className="absolute inset-0 blur-3xl bg-[#2d6cdf]/10 rounded-full scale-[1.8] animate-pulse-slow pointer-events-none" style={{ animationDelay: '1s' }} />

            {/* موجات الإشارة — أكبر وأوضح */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[440px] md:h-[440px] pointer-events-none rotate-12">
              <div className="absolute inset-0 border-t-2 border-r-2 border-[#8CC63F]/50 rounded-full animate-signal" style={{ animationDelay: "0s" }} />
              <div className="absolute inset-0 border-t-2 border-r-2 border-[#4AB478]/30 rounded-full animate-signal" style={{ animationDelay: "0.6s" }} />
              <div className="absolute inset-0 border-t-2 border-r-2 border-[#4AB478]/10 rounded-full animate-signal" style={{ animationDelay: "1.2s" }} />
            </div>

            {/* حلقة دوارة رفيعة حوالين اللوجو */}
            {!isMobile && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] h-[220px] md:w-[300px] md:h-[300px] pointer-events-none">
                <motion.div
                  className="w-full h-full rounded-full"
                  style={{
                    background: 'conic-gradient(from 0deg, transparent 0%, rgba(74,180,120,0.3) 15%, transparent 30%, transparent 60%, rgba(45,108,223,0.25) 75%, transparent 90%)',
                    maskImage: 'radial-gradient(circle, transparent 68%, black 69%, black 71%, transparent 72%)',
                    WebkitMaskImage: 'radial-gradient(circle, transparent 68%, black 69%, black 71%, transparent 72%)',
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                />
              </div>
            )}

            {/* اللوجو نفسه — أكبر + Float مستمر + Parallax */}
            <motion.div
              className="relative w-40 h-40 md:w-56 md:h-56 mx-auto z-10 cursor-pointer"
              style={isMobile ? undefined : { x: logoX, y: logoY }}
              animate={isMobile ? undefined : { y: [0, -12, 0] }}
              transition={isMobile ? undefined : { duration: 4, repeat: Infinity, ease: "easeInOut" }}
              whileHover={isMobile ? undefined : { scale: 1.08, rotate: [0, -2, 2, 0], transition: { duration: 0.5 } }}
            >
              <img
                src="/logo.png"
                alt="Ping After Logo"
                className="w-full h-full object-contain drop-shadow-[0_0_40px_rgba(74,180,120,0.55)]"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* ── العنوان: حرف-بحرف + تدرجات من ألوان اللوجو ── */}
        <motion.h1
          variants={letterContainer}
          initial="hidden"
          animate={loaded ? "visible" : "hidden"}
          className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight mb-6 select-none"
          style={{ perspective: '1000px' }}
        >
          {/* "Ping" — تدرج أزرق زي حرف P في الأيقونة */}
          {"Ping".split("").map((char, i) => (
            <motion.span
              key={`p-${i}`}
              variants={letterVariant}
              className="inline-block bg-clip-text text-transparent animate-gradient-x"
              style={{
                backgroundImage: `linear-gradient(120deg, #ffffff 0%, #5b9bd5 40%, #2d6cdf 70%, #5b9bd5 100%)`,
                backgroundSize: '200% 100%',
              }}
            >
              {char}
            </motion.span>
          ))}
          {/* "After" — تدرج أخضر → ليموني زي إشارة الواي فاي */}
          {"After".split("").map((char, i) => (
            <motion.span
              key={`a-${i}`}
              variants={letterVariant}
              className="inline-block bg-clip-text text-transparent animate-gradient-x"
              style={{
                backgroundImage: `linear-gradient(120deg, #4AB478 0%, #8CC63F 40%, #A3E635 70%, #4AB478 100%)`,
                backgroundSize: '200% 100%',
                animationDelay: '0.5s',
                filter: 'drop-shadow(0 0 25px rgba(140,198,63,0.35))',
              }}
            >
              {char}
            </motion.span>
          ))}
        </motion.h1>

        {/* خط فاصل متوهج تحت العنوان */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={loaded ? { scaleX: 1, opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="h-px w-48 md:w-72 mb-8"
          style={{
            background: 'linear-gradient(90deg, transparent, #2d6cdf 30%, #4AB478 70%, transparent)',
            boxShadow: '0 0 20px rgba(74,180,120,0.5)',
          }}
        />

        {/* ── الـ Hook: سطرين + كلمة-كلمة + كلمات ملونة ── */}
        <motion.div
          variants={wordContainer}
          initial="hidden"
          animate={loaded ? "visible" : "hidden"}
          className="max-w-3xl mx-auto"
        >
          {HOOK_LINES.map((line, lineIdx) => (
            <p
              key={lineIdx}
              className={`font-light tracking-wide leading-snug ${
                lineIdx === 0
                  ? 'text-2xl md:text-4xl text-white/70'
                  : 'text-2xl md:text-4xl text-white/70 mt-1'
              }`}
            >
              {line.map((word, wIdx) => (
                <motion.span
                  key={wIdx}
                  variants={wordVariant}
                  className={`inline-block mr-[0.3em] ${
                    word.highlight === 'blue'
                      ? 'font-semibold bg-clip-text text-transparent'
                      : word.highlight === 'green'
                      ? 'font-semibold bg-clip-text text-transparent'
                      : ''
                  }`}
                  style={
                    word.highlight === 'blue'
                      ? { backgroundImage: 'linear-gradient(120deg, #5b9bd5, #2d6cdf)' }
                      : word.highlight === 'green'
                      ? {
                          backgroundImage: 'linear-gradient(120deg, #4AB478, #A3E635)',
                          filter: 'drop-shadow(0 0 15px rgba(140,198,63,0.4))',
                        }
                      : undefined
                  }
                >
                  {word.text}
                </motion.span>
              ))}
            </p>
          ))}
        </motion.div>
      </motion.div>

      {/* ═══════════ Trust Metrics ═══════════ */}
      <motion.div
        id="trust-metrics"
        className="container mx-auto px-6 max-w-6xl relative z-20 mt-8"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {/* زر CTA محسّن */}
        <motion.div variants={itemVariants} className="flex justify-center mb-16">
          <motion.button
            onClick={() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="group relative px-10 py-5 rounded-full text-white font-semibold text-base tracking-wide overflow-hidden"
            style={{
              background: 'linear-gradient(120deg, #4AB478, #3ca068)',
              boxShadow: '0 0 30px rgba(74,180,120,0.3)',
            }}
          >
            {/* لمعة تعدي على الزر */}
            {!isMobile && (
              <motion.span
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.25) 50%, transparent 60%)',
                }}
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }}
              />
            )}
            <span className="relative flex items-center gap-2">
              Join the Waitlist
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </span>
          </motion.button>
        </motion.div>

        <motion.div variants={itemVariants} className="text-center mb-12">
          <span className="text-xs font-semibold text-white/40 uppercase tracking-[0.2em]">
            Built on trust
          </span>
        </motion.div>

        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 mb-8">
          {TRUST_BADGES.map((badge, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              className="flex items-center gap-3 text-white/60 group bg-white/5 px-6 py-4 rounded-2xl border border-white/10 hover:border-[#4AB478]/40 hover:bg-white/10 hover:shadow-[0_10px_40px_-10px_rgba(74,180,120,0.3)] transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-[#4AB478]/20 transition-colors border border-white/5 group-hover:scale-110 duration-300">
                <badge.icon className="w-5 h-5 text-white/40 group-hover:text-[#8CC63F] transition-colors" />
              </div>
              <div className="text-left">
                <span className="text-sm font-semibold text-white block leading-tight">{badge.label}</span>
                <span className="text-[10px] text-white/50 uppercase tracking-wider">{badge.detail}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ═══════════ Scroll Indicator ═══════════ */}
      <AnimatePresence>
        {!hasScrolledPast && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 2.5, duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 0.5 } }}
            className="fixed bottom-10 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-2 text-white/40"
            >
              <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}